import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useCampaignsInfinite } from "../api/useCampaignsInfinite";
import { campaignFilterStatus } from "../contstants";
import CampaignList from "./CampaignList/CampaignList";

import {
  FiltersDrawer,
  type FilterSectionConfig,
} from "@/components/ui/v2/filters/FiltersDrawer";
import {
  TableActiveFilters,
  type ActiveFilter,
} from "@/components/ui/v2/table/table-active-filters";
import { TableToolbar } from "@/components/ui/v2/table/table-toolbar";
import { clearFilterValues, removeFilterValue } from "@/lib/utils/filterParams";

const FILTER_KEY = "status";

const STATUS_LABEL_KEYS: Record<(typeof campaignFilterStatus)[number], string> = {
  active: "common.active",
  paused: "common.paused",
  inactive: "common.inactive",
};

type CampaignsContentProps = {
  clientId?: string;
  descending: boolean | undefined;
  orderBy?: string;
  queryString: string;
  search: string;
  searchValue: string;
  setSearch: (search: string) => void;
};

function CampaignsContent({
  clientId,
  descending,
  orderBy,
  queryString,
  search,
  searchValue,
  setSearch,
}: CampaignsContentProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const selectedStatuses = useMemo<(typeof campaignFilterStatus)[number][]>(() => {
    const values = searchParams.getAll(FILTER_KEY) as (typeof campaignFilterStatus)[number][];
    return values.filter((s) => campaignFilterStatus.includes(s));
  }, [searchParams]);

  const {
    data: campaignsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCampaignsInfinite({
    clientId,
    descending,
    orderBy,
    search: search || undefined,
    includeIntegrations: true,
    statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
  });

  const campaigns = useMemo(() => {
    if (!campaignsData?.pages) return [];
    return campaignsData.pages.flatMap((page) => page.data);
  }, [campaignsData]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const filterSections: FilterSectionConfig[] = useMemo(
    () => [
      {
        key: FILTER_KEY,
        title: "common.status",
        options: campaignFilterStatus.map((status) => ({
          label: t(STATUS_LABEL_KEYS[status]),
          value: status,
        })),
      },
    ],
    [t],
  );

  const activeFilters: ActiveFilter[] = useMemo(
    () =>
      selectedStatuses.map((status) => ({
        key: FILTER_KEY,
        label: t("common.status"),
        value: status,
        valueLabel: t(STATUS_LABEL_KEYS[status]),
      })),
    [selectedStatuses, t],
  );

  const handleRemoveFilter = useCallback(
    (filter: ActiveFilter) => {
      setSearchParams(removeFilterValue(searchParams, filter.key, filter.value.toString()));
    },
    [searchParams, setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(clearFilterValues(searchParams, [FILTER_KEY]));
  }, [searchParams, setSearchParams]);

  return (
    <>
      <TableToolbar
        onSearchChange={setSearch}
        searchValue={searchValue}
        onFiltersClick={() => setIsFiltersOpen(true)}
      />

      {activeFilters.length > 0 && (
        <TableActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearFilters={handleClearFilters}
        />
      )}

      <CampaignList
        campaigns={campaigns}
        queryString={queryString}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={handleLoadMore}
      />

      <FiltersDrawer
        open={isFiltersOpen}
        onOpenChange={setIsFiltersOpen}
        searchParams={searchParams}
        filterKeys={[FILTER_KEY]}
        filterSections={filterSections}
        entityName={t("common.campaigns")}
        defaultExpanded
      />
    </>
  );
}

export default CampaignsContent;
